import { useState, useEffect, useRef } from 'react';
import { RadioButton } from 'primereact/radiobutton';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Panel } from 'primereact/panel';
import { ChartAreaIcon, Info, Target } from 'lucide-react';
import { Divider } from 'primereact/divider';
import { MultiSelect } from 'primereact/multiselect';
import { InputNumber } from 'primereact/inputnumber';
import { Tooltip } from 'primereact/tooltip';

import {
  ORDER_BY,
  PERFORMANCE_TYPES,
  periodComparisonOptions,
} from '../constant/constant';

const WidgetDetails = ({
  dimensionList,
  measureList,
  action,
  widgetFormik,
  tableList,
  setSelectedChartTableValue,
  comparisonDimensionList,
}) => {
  const [selectedMeasures, setSelectedMeasures] = useState([]);

  const buildGroupedOptions = dimeList => {
    const grouped = {};

    dimeList.forEach(item => {
      if (item.type !== 'time') return;

      if (!grouped[item.firstLetter]) {
        grouped[item.firstLetter] = [];
      }

      grouped[item.firstLetter].push({
        label: item.title,
        value: item.name,
        raw: item,
      });
    });

    return Object.keys(grouped).map(group => ({
      label: '',
      items: grouped[group],
    }));
  };

  const initiateData = () => {
    const selectedDimension = measureList?.find(
      a => a.name === widgetFormik.values.chart_configuration.dimension
    );
    if (selectedDimension?.measures) {
      setSelectedMeasures(selectedDimension.measures);
    }
  };

  useEffect(() => {
    if (action !== 'create') {
      initiateData();
    }
  }, []);
  const overlayRef = useRef(null);
  const headerTemplate = () => {
    return (
      <div className="flex align-items-center gap-2 text-sm font-medium text-gray-700">
        <ChartAreaIcon className="text-primary text-sm w-1rem h-1rem" />
        <span className="text-sm font-bold text-gray-500">
          Chart Configuration
        </span>
      </div>
    );
  };
  const performanceHeaderTemplate = () => {
    return (
      <div className="flex align-items-center gap-2 text-sm font-medium text-gray-700">
        <Target className="text-primary text-sm w-1rem h-1rem" />
        <span className="text-sm font-bold text-gray-500">
          Performance Target
        </span>
      </div>
    );
  };

  return (
    <>
      <Panel
        header={headerTemplate}
        className="w-full"
        pt={{ content: { className: 'p-2' } }}
      >
        <div className="grid w-full border-1 border-solid border-round border-200 grid-nogutter">
          <div className="grid grid-nogutter col md:col-3 p-2 gap-2 align-items-center">
            <div className="col-10 p-0">
              <span className="p-float-label">
                <Dropdown
                  showClear
                  filter
                  value={
                    widgetFormik?.values?.custom_properties?.selectedChartTable
                  }
                  id="chartTable"
                  options={tableList}
                  optionLabel="value"
                  optionValue="value"
                  disabled={action === 'view'}
                  className="w-full"
                  onChange={event => {
                    if (event?.value) {
                      widgetFormik.setTouched({
                        ...widgetFormik.touched,
                        chart_configuration: true,
                      });
                    }
                    setSelectedChartTableValue(event?.value || '', {
                      ...widgetFormik.values,
                      custom_properties: {
                        ...widgetFormik.values.custom_properties,
                        selectedChartTable: event?.value || '',
                      },
                    });
                  }}
                />
                <label htmlFor="chartTable">
                  Data Source <span className="text-red-400">*</span>
                </label>
              </span>
            </div>
            <div className="col-1 p-0">
              <Info
                className="text-primary text-sm w-1rem h-1rem data-source-icon"
                data-pr-tooltip="Select a Data Source to configure chart"
                data-pr-position="bottom"
              />
              <Tooltip
                target=".data-source-icon"
                pt={{
                  root: { className: 'p-2' },
                  text: { className: 'text-xs' },
                }}
              />
            </div>
          </div>
          <Divider layout="vertical" className="block" />
          <div className="col grid grid-nogutter p-2 gap-2 align-items-center">
            <div className="grid grid-nogutter col-12 md:col-4 gap-1 align-items-baseline">
              <div className="col-10 p-0">
                <span className="p-float-label">
                  <Dropdown
                    showClear
                    filter
                    id="field"
                    options={measureList || []}
                    disabled={action === 'view' ? true : false}
                    value={
                      measureList?.find(
                        a =>
                          a.name ===
                          widgetFormik.values.chart_configuration.dimension
                      )?.value
                    }
                    optionLabel="value"
                    className="w-full"
                    onChange={async event => {
                      const selectedLabel = event.value;

                      const fullObject = measureList.find(
                        item => item.value === selectedLabel
                      );

                      if (fullObject?.name) {
                        widgetFormik.setTouched({
                          ...widgetFormik.touched,
                          chart_configuration: true,
                        });
                      } else {
                        widgetFormik.setTouched({
                          ...widgetFormik.touched,
                          chart_configuration: false,
                        });
                      }
                      setSelectedMeasures(fullObject?.measures);
                      let isPrimaryKeySelected = '';
                      if (fullObject?.name && fullObject?.primaryKey) {
                        isPrimaryKeySelected =
                          fullObject.name === fullObject.primaryKey;
                      }
                      let summarizedBy = '';
                      if (isPrimaryKeySelected) {
                        summarizedBy = fullObject?.primaryKeyMeasures?.find(
                          mKey => mKey.aggType === 'count'
                        )?.name;
                      }
                      await widgetFormik.setFieldValue('chart_configuration', {
                        ...widgetFormik.values.chart_configuration,
                        dimension: fullObject?.name,
                        measureSummarizedBy: summarizedBy,
                        isPrimaryKeySelected: isPrimaryKeySelected,
                      });
                      await widgetFormik.setFieldValue('custom_properties', {
                        ...widgetFormik.values.custom_properties,
                        chartMeta: fullObject?.meta || null,
                      });
                      const path = `chart_configuration`;
                      requestAnimationFrame(() =>
                        widgetFormik.validateField(path)
                      );
                    }}
                  />
                  <label htmlFor="field">
                    Field <span className="text-red-400">*</span>
                  </label>
                </span>
              </div>
              <div className="col-1 p-0">
                <Info
                  className="text-primary text-sm w-1rem h-1rem main-field-icon"
                  data-pr-tooltip="Select the main field for the chart"
                  data-pr-position="bottom"
                />
                <Tooltip
                  target=".main-field-icon"
                  pt={{
                    root: { className: 'p-2' },
                    text: { className: 'text-xs' },
                  }}
                />
              </div>
            </div>
            <div className="grid grid-nogutter col-12 md:col-4 gap-1 align-items-baseline">
              <div className="col-10">
                <span className="p-float-label">
                  <Dropdown
                    showClear
                    filter
                    id="dimensionMeasures"
                    options={selectedMeasures || []}
                    disabled={action === 'view' ? true : false}
                    optionLabel={option =>
                      option?.aggType
                        ? option.aggType.charAt(0).toUpperCase() +
                          option.aggType.slice(1)
                        : ''
                    }
                    className="w-full"
                    value={
                      selectedMeasures?.find(
                        a =>
                          a.name ===
                          widgetFormik.values.chart_configuration
                            .measureSummarizedBy
                      ) || ''
                    }
                    onChange={event => {
                      widgetFormik.setFieldValue('chart_configuration', {
                        ...widgetFormik.values.chart_configuration,
                        measureSummarizedBy: event?.target?.value?.name || '',
                      });
                      widgetFormik.setFieldValue('custom_properties', {
                        ...widgetFormik.values.custom_properties,
                        chartMeta: widgetFormik.values?.custom_properties
                          ?.chartMeta
                          ? {
                              ...widgetFormik.values.custom_properties
                                .chartMeta,
                              aggType: event?.target?.value.aggType,
                            }
                          : null,
                      });
                    }}
                  />
                  <label htmlFor="dimensionMeasures">Summarized By</label>
                </span>
              </div>
              <div className="col-1">
                <Info
                  className="text-primary text-sm w-1rem h-1rem dimension-icon"
                  data-pr-tooltip="Choose an aggregate function"
                  data-pr-position="bottom"
                />
                <Tooltip
                  target=".dimension-icon"
                  pt={{
                    root: { className: 'p-2' },
                    text: { className: 'text-xs' },
                  }}
                />
              </div>
            </div>
            {widgetFormik.values.widget_type !== 'gauge' &&
              widgetFormik.values.widget_type !== 'card' &&
              widgetFormik.values.widget_type !== 'progress-bar' &&
              !widgetFormik.values.is_third_party && (
                <>
                  {widgetFormik.values.widget_type !== 'stacked-bar' &&
                    widgetFormik.values.widget_type !== 'grouped-bar' && (
                      <div className="grid grid-nogutter col-12 md:col-3 gap-1 align-items-baseline">
                        <div className="col-10 p-0">
                          <span className="p-float-label">
                            <MultiSelect
                              filter
                              id="groupBy"
                              name="groupBy"
                              options={dimensionList}
                              optionLabel="value"
                              className="w-full"
                              pt={{
                                label: {
                                  className: 'text-xs',
                                },
                              }}
                              disabled={action === 'view' ? true : false}
                              value={
                                widgetFormik?.values?.chart_configuration?.groupBy?.map(
                                  obj => obj.value
                                ) || []
                              }
                              maxSelectedLabels={1}
                              display="comma"
                              onChange={event => {
                                const selectedValues = event.value;
                                const selectedObjects = dimensionList?.filter(
                                  item => selectedValues.includes(item.value)
                                );

                                widgetFormik.setFieldValue(
                                  'chart_configuration',
                                  {
                                    ...widgetFormik.values.chart_configuration,
                                    groupBy: selectedObjects,
                                    orderByField: selectedObjects.length
                                      ? selectedObjects[0].name
                                      : '',
                                  }
                                );
                              }}
                            />
                            <label htmlFor="field">Group By</label>
                          </span>
                        </div>
                        <div className="col-1 p-0">
                          <Info
                            className="text-primary text-sm w-1rem h-1rem group-field-icon"
                            data-pr-tooltip="  Select a field to group data by"
                            data-pr-position="bottom"
                          />
                          <Tooltip
                            target=".group-field-icon"
                            pt={{
                              root: { className: 'p-2' },
                              text: { className: 'text-xs' },
                            }}
                          />
                        </div>
                      </div>
                    )}
                  {(widgetFormik.values.widget_type === 'stacked-bar' ||
                    widgetFormik.values.widget_type === 'grouped-bar') && (
                    <>
                      <div className="grid grid-nogutter col-12 md:col-4 gap-1 align-items-baseline">
                        <div className="col-10 p-0">
                          <span className="p-float-label">
                            <Dropdown
                              filter
                              id="primaryGroupBy"
                              name="primaryGroupBy"
                              options={dimensionList}
                              optionLabel="value"
                              className="w-full"
                              pt={{ tokenLabel: { className: 'text-xs' } }}
                              disabled={action === 'view' ? true : false}
                              value={
                                widgetFormik?.values?.chart_configuration
                                  ?.groupBy?.[0]?.value
                              }
                              onChange={event => {
                                const selectedValues = event.value;

                                const selectedObjects = dimensionList?.filter(
                                  item => selectedValues === item.value
                                );

                                const newGroupBy = [
                                  ...widgetFormik.values.chart_configuration
                                    .groupBy,
                                ];
                                newGroupBy[0] = selectedObjects?.[0] || null;
                                if (!newGroupBy[1]) newGroupBy[1] = null;

                                widgetFormik.setFieldValue(
                                  'chart_configuration',
                                  {
                                    ...widgetFormik.values.chart_configuration,
                                    groupBy: newGroupBy.filter(Boolean),
                                    orderByField: selectedObjects.length
                                      ? selectedObjects[0].name
                                      : '',
                                  }
                                );
                              }}
                            />
                            <label htmlFor="field">
                              Primary Group By <i className="text-red-400">*</i>
                            </label>
                          </span>
                        </div>
                        <div className="col-1 p-0">
                          <Info
                            className="text-primary text-sm w-1rem h-1rem group-field-icon"
                            data-pr-tooltip="  Select a primary field to group data by"
                            data-pr-position="bottom"
                          />
                          <Tooltip
                            target=".group-field-icon"
                            pt={{
                              root: { className: 'p-2' },
                              text: { className: 'text-xs' },
                            }}
                          />
                        </div>
                      </div>
                      <div className="grid grid-nogutter col-12 md:col-4 gap-1 align-items-baseline">
                        <div className="col-10 p-0">
                          <span className="p-float-label">
                            <Dropdown
                              filter
                              id="secondaryGroupBy"
                              options={dimensionList.filter(
                                dim =>
                                  dim.value !==
                                  widgetFormik.values.chart_configuration
                                    .groupBy[0]?.value
                              )}
                              disabled={
                                action === 'view' ||
                                !widgetFormik.values.chart_configuration
                                  .groupBy[0]
                              }
                              name="secondaryGroupBy"
                              optionLabel="value"
                              className="w-full"
                              pt={{ tokenLabel: { className: 'text-xs' } }}
                              value={
                                widgetFormik?.values?.chart_configuration
                                  ?.groupBy?.[1]?.value
                              }
                              onChange={event => {
                                const selectedValues = event.value;

                                const selectedObjects = dimensionList?.filter(
                                  item => selectedValues === item.value
                                );

                                const newGroupBy = [
                                  ...widgetFormik.values.chart_configuration
                                    .groupBy,
                                ];
                                newGroupBy[1] = selectedObjects?.[0] || null;
                                widgetFormik.setFieldValue(
                                  'chart_configuration',
                                  {
                                    ...widgetFormik.values.chart_configuration,
                                    groupBy: newGroupBy.filter(Boolean),
                                  }
                                );
                              }}
                            />
                            <label htmlFor="field">
                              Secondary Group By
                              <i className="text-red-400">*</i>
                            </label>
                          </span>
                        </div>
                        <div className="col-1 p-0">
                          <Info
                            className="text-primary text-sm w-1rem h-1rem group-field-icon"
                            data-pr-tooltip="  Select a secondary field to group data by"
                            data-pr-position="bottom"
                          />
                          <Tooltip
                            target=".group-field-icon"
                            pt={{
                              root: { className: 'p-2' },
                              text: { className: 'text-xs' },
                            }}
                          />
                        </div>
                      </div>
                    </>
                  )}
                  {widgetFormik.values?.chart_configuration?.groupBy?.length >
                    0 && (
                    <>
                      <div className="grid grid-nogutter col-12 md:col-4 gap-1 align-items-baseline">
                        <div className="col-10 p-0">
                          <span className="p-float-label">
                            <Dropdown
                              showClear
                              filter
                              id="chartOrderByField"
                              options={dimensionList.filter(orderByObject => {
                                const foundObject =
                                  widgetFormik.values.chart_configuration.groupBy.find(
                                    groupKey =>
                                      groupKey.name === orderByObject.name
                                  );
                                if (foundObject) {
                                  return orderByObject;
                                }
                              })}
                              disabled={action === 'view' ? true : false}
                              optionLabel="value"
                              className="w-full"
                              value={
                                dimensionList?.find(
                                  a =>
                                    a.name ===
                                    widgetFormik.values?.chart_configuration
                                      ?.orderByField
                                )?.value
                              }
                              onChange={event => {
                                const selectedLabel = event.value;

                                const fullObject = dimensionList.find(
                                  item => item.value === selectedLabel
                                );
                                widgetFormik.setFieldValue(
                                  'chart_configuration',
                                  {
                                    ...widgetFormik.values.chart_configuration,
                                    orderByField: fullObject?.name || '',
                                  }
                                );
                              }}
                            />
                            <label htmlFor="chartOrderByField">
                              Order By Field <i className="text-red-400">*</i>
                            </label>
                          </span>
                        </div>
                        <div className="col-1 p-0">
                          <Info
                            className="text-primary text-sm w-1rem h-1rem sort-field-icon"
                            data-pr-tooltip="Select the field to sort by"
                            data-pr-position="bottom"
                          />
                          <Tooltip
                            target=".sort-field-icon"
                            pt={{
                              root: { className: 'p-2' },
                              text: { className: 'text-xs' },
                            }}
                          />
                        </div>
                      </div>
                      <div className="grid grid-nogutter col-12 md:col-4 gap-1 align-items-baseline">
                        <div className="col-10 p-0">
                          <span className="p-float-label">
                            <Dropdown
                              showClear
                              filter
                              id="chartOrderBy"
                              name="chartOrderBy"
                              key={
                                widgetFormik.values.chart_configuration.orderBy
                              }
                              options={ORDER_BY}
                              disabled={action === 'view' ? true : false}
                              optionLabel="value"
                              value={
                                ORDER_BY.find(
                                  record =>
                                    record.name ===
                                    widgetFormik.values.chart_configuration
                                      .orderBy
                                )?.value
                              }
                              onChange={event => {
                                const selectedLabel = event.value;

                                const fullObject = ORDER_BY.find(
                                  item => item.value === selectedLabel
                                );
                                widgetFormik.setFieldValue(
                                  'chart_configuration',
                                  {
                                    ...widgetFormik.values.chart_configuration,
                                    orderBy: fullObject?.name || '',
                                  }
                                );
                              }}
                              className="w-full"
                            />
                            <label htmlFor="chartOrderByField">
                              Order By <i className="text-red-400">*</i>
                            </label>
                          </span>
                        </div>
                        <div className="col-1 p-0">
                          <Info
                            className="text-primary text-sm w-1rem h-1rem sort-direction-icon"
                            data-pr-tooltip="Pick the sort direction (ascending or
                                descending)"
                            data-pr-position="bottom"
                          />
                          <Tooltip
                            target=".sort-direction-icon"
                            pt={{
                              root: { className: 'p-2' },
                              text: { className: 'text-xs' },
                            }}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
          </div>
        </div>
      </Panel>

      {['gauge', 'progress-bar', 'card'].includes(
        widgetFormik.values.widget_type
      ) && (
        <Panel
          header={performanceHeaderTemplate}
          className="w-full"
          pt={{ content: { className: 'p-2' } }}
        >
          <div className="mt-3">
            <div className="grid grid-nogutter text-sm text-gray-700 font-bold mb-3 gap-6 align-items-center">
              <div className="flex gap-2 align-items-center">
                <OverlayPanel
                  ref={overlayRef}
                  pt={{
                    content: { className: 'p-1 text-sm' },
                    root: { className: 'shadow-3' },
                  }}
                >
                  <ul>
                    <li>
                      Turn this option on or off to show performance comparison.
                    </li>
                    <li>
                      For detailed performance insights, increase the widget
                      size on the dashboard.
                    </li>
                  </ul>
                </OverlayPanel>
                <InputSwitch
                  checked={
                    widgetFormik?.values?.custom_properties?.showPerformance
                  }
                  className="custom-tooltip"
                  onChange={event => {
                    widgetFormik.setFieldValue('custom_properties', {
                      ...widgetFormik.values.custom_properties,
                      showPerformance: event?.value || false,
                    });
                  }}
                />
                Show Performance
                <i
                  className="pi pi-info-circle text-sm text-primary cursor-pointer"
                  onMouseEnter={e => overlayRef.current.show(e)}
                />
              </div>

              {widgetFormik?.values?.custom_properties?.showPerformance && (
                <div className="flex align-items-center gap-3">
                  {PERFORMANCE_TYPES.map(option => (
                    <div key={option.value} className="flex align-items-center">
                      <RadioButton
                        inputId={option.value}
                        name="performanceType"
                        value={option.value}
                        onChange={e =>
                          widgetFormik.setFieldValue('custom_properties', {
                            ...widgetFormik.values.custom_properties,
                            performanceType: e.value,
                          })
                        }
                        checked={
                          widgetFormik?.values?.custom_properties
                            ?.performanceType === option.value
                        }
                      />
                      <label htmlFor={option.value} className="ml-2 text-sm">
                        {option.label}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {widgetFormik?.values?.custom_properties?.showPerformance &&
              widgetFormik?.values?.custom_properties?.performanceType ===
                'fixedTarget' && (
                <span className="p-float-label">
                  <InputNumber
                    inputId="customProperties.target"
                    value={
                      widgetFormik.values?.chart_configuration?.measureTarget
                    }
                    onValueChange={event => {
                      widgetFormik.setFieldValue('chart_configuration', {
                        ...widgetFormik.values.chart_configuration,
                        measureTarget: event.value,
                      });
                    }}
                  />
                  <label htmlFor="customProperties.target">Target</label>
                </span>
              )}

            {widgetFormik?.values?.custom_properties?.showPerformance &&
              widgetFormik?.values?.custom_properties?.performanceType ===
                'periodComparison' && (
                <div className="flex w-5 gap-2">
                  <Dropdown
                    showClear
                    filter
                    value={
                      widgetFormik?.values?.custom_properties?.compareField
                    }
                    options={buildGroupedOptions(comparisonDimensionList)}
                    onChange={event => {
                      widgetFormik.setValues({
                        ...widgetFormik.values,
                        custom_properties: {
                          ...widgetFormik.values.custom_properties,
                          compareField: event.target.value,
                        },
                        chart_configuration: {
                          ...widgetFormik.values.chart_configuration,
                          measureTarget: '',
                        },
                      });
                    }}
                    optionLabel="label"
                    optionValue="value"
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                    placeholder="Compare Field"
                    className="w-full"
                  />

                  <Dropdown
                    showClear
                    onChange={event => {
                      widgetFormik.setFieldValue('custom_properties', {
                        ...widgetFormik.values.custom_properties,
                        compareOperator: event.target.value,
                      });
                    }}
                    value={
                      widgetFormik?.values?.custom_properties?.compareOperator
                    }
                    options={periodComparisonOptions}
                    placeholder="Compare Against"
                    filter
                    filterDelay={400}
                    className="w-full"
                    optionLabel="label"
                  />
                </div>
              )}
          </div>
        </Panel>
      )}
    </>
  );
};

export default WidgetDetails;
